// ////////////////////////////////// Consent //////////////////////////////////
//
// Cookie + CMP primitives shared by the privacy gate (first-visit modal) and
// the /privacy-choices toggle. Ported from GunBuyBack's privacy_gate.js so the
// two properties behave identically.
//
// Consent Mode is entirely Usercentrics' job, via its GTM tag: it emits the
// 'denied' default and every update. Nothing here pushes Consent Mode itself,
// so the signal Google gets can never disagree with what the CMP recorded. A
// choice made before the CMP arrives is re-asserted from the cookie on the next
// load.

const REMEMBER_DAYS = 30
const PASSED = "1"

// All three come from config/services.php via <body> (see app.blade.php): the
// full cookie name for the current gate version, the prefix used to sweep
// cookies from older versions, and the domain the cookie is scoped to. Bumping
// the version changes the name, so every old cookie is ignored + swept and the
// gate re-appears for all users on their next visit.
function gateAttr(name) {
  return document.body?.getAttribute(name) || ""
}

export function gateCookieName() {
  return gateAttr("data-gate-cookie")
}

function cookiePrefix() {
  return gateAttr("data-gate-cookie-prefix")
}

// --- Cookies ---------------------------------------------------------------

// Scope the cookie to the registrable domain so the gate/opt-out choice is
// shared across the apex and every subdomain (www, kp, ...). Config leaves the
// domain empty for local and tunnel hosts, and we also omit it when the current
// host falls outside that domain — a mismatched Domain attribute is rejected
// outright, which would silently drop the choice.
function cookieDomain() {
  const domain = gateAttr("data-gate-cookie-domain")
  if (!domain) return ""

  const host = window.location.hostname
  if (host === domain || host.endsWith(`.${domain}`)) return `;Domain=.${domain}`
  return ""
}

function setCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  const secure = window.location.protocol === "https:" ? ";Secure" : ""
  document.cookie = `${name}=${value};path=/;SameSite=Lax${cookieDomain()};expires=${expires}${secure}`
}

function getCookie(name) {
  return document.cookie.split(";").reduce((found, part) => {
    const [key, value] = part.trim().split("=")
    return key === name ? value : found
  }, null)
}

// The single versioned cookie carries BOTH facts: that the visitor passed the
// gate AND their sharing choice. Value is exactly "passed:share" where share is
// "in" (sharing allowed) or "out" (opted out). The /privacy-choices toggle
// writes the same cookie, so both entry points share one store — exactly what
// the page text promises ("stored in a browser cookie").
export function writeGateChoice(optedIn) {
  setCookie(gateCookieName(), `${PASSED}:${optedIn ? "in" : "out"}`, REMEMBER_DAYS)
}

// Returns "in", "out", or null. Only the exact "passed:share" format counts as
// passed — any other value decodes to null and re-triggers the gate.
export function readGateChoice() {
  const raw = getCookie(gateCookieName())
  if (!raw) return null

  const [passed, share] = raw.split(":")
  if (passed !== PASSED) return null
  if (share === "in") return "in"
  if (share === "out") return "out"
  return null
}

// Drop gate cookies from older versions so they don't pile up on a bump.
export function sweepOldCookies(current) {
  const prefix = cookiePrefix()
  if (!prefix) return

  document.cookie.split(";").forEach((part) => {
    const name = part.trim().split("=")[0]
    if (name.startsWith(prefix) && name !== current) {
      // Clear both the domain-scoped cookie (what we write now) and any legacy
      // host-only cookie left by a version predating the domain scoping.
      const expired = "=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT"
      document.cookie = `${name}${expired}${cookieDomain()}`
      document.cookie = `${name}${expired}`
    }
  })
}

export function gpcEnabled() {
  return navigator.globalPrivacyControl === true
}

// --- Usercentrics bridge ---------------------------------------------------

// UC_UI_INITIALIZED fires once per page load. This is an Inertia SPA, so a
// client-side navigation can call whenCmpReady long after that — hence the
// module-level latch, which GunBuyBack does not need because every page there
// is a full load.
let cmpInitialized = false

if (typeof window !== "undefined") {
  window.addEventListener(
    "UC_UI_INITIALIZED",
    () => {
      cmpInitialized = true
    },
    { once: true }
  )
}

// Calls back with __ucCmp once the CMP is initialized, and never otherwise — if
// it never loads there is nothing to hand it. UC_UI_INITIALIZED fires at the
// moment isInitialized() flips, so there is nothing to poll for; the
// isInitialized() check only covers the CMP having beaten this bundle.
export function whenCmpReady(cb) {
  let settled = false

  function settle() {
    if (settled || !window.__ucCmp) return
    settled = true
    cmpInitialized = true
    try {
      cb(window.__ucCmp)
    } catch {
      /* never let CMP wiring break the gate */
    }
  }

  if (cmpInitialized) {
    settle()
    return
  }

  window.addEventListener("UC_UI_INITIALIZED", settle, { once: true })

  Promise.resolve(window.__ucCmp?.isInitialized?.())
    .then((ok) => {
      if (ok) settle()
    })
    .catch(() => {})
}

// The blocked-script listener in app.blade.php is attached before GTM or the
// CMP can load, so a content blocker taking out either is observable here.
export function whenCmpBlocked(cb) {
  if (window.__ucBlocked) {
    cb()
    return
  }
  window.addEventListener("uc:blocked", () => cb(), { once: true })
}

// acceptAllConsents/denyAllConsents persist the consent record AND trigger the
// account's Google Consent Mode integration, which is what pushes
// gtag('consent','update',...). We never push one ourselves, so there is
// exactly one writer.
export function acceptAll() {
  whenCmpReady((cmp) => cmp.acceptAllConsents?.())
}

export function optOutAll() {
  whenCmpReady((cmp) => cmp.denyAllConsents?.())
}

// `essential === false` rather than `!essential`, so a missing flag is not read
// as non-essential.
export function getOptedIn(cb) {
  whenCmpReady((cmp) => {
    if (!cmp.getConsentDetails) return
    Promise.resolve(cmp.getConsentDetails())
      .then((details) => {
        const services = details?.services || {}
        cb(
          Object.values(services).some(
            (service) => service?.consent?.given && service?.essential === false
          )
        )
      })
      .catch(() => {})
  })
}

// The CMP only scans for .uc-embed nodes during its own init. This is an Inertia
// SPA, so an embed mounted by a client-side navigation is a node it has never
// seen.
export function hydrateEmbeddings() {
  whenCmpReady((cmp) => cmp.hydrateEmbeddings?.())
}

// Apply a stored choice. GPC always wins: a visitor who continued before
// enabling GPC is re-applied as opted out on every subsequent visit.
//
// The CMP persists its own consent record, so a returning visitor is already
// restored by the time this runs; re-asserting the stored choice keeps the CMP
// and our gate cookie from drifting apart (e.g. after a GPC change).
export function applyChoice(optedIn) {
  if (optedIn && !gpcEnabled()) {
    acceptAll()
  } else {
    optOutAll()
  }
}
